import { inject, Injectable } from '@angular/core';
import { PaginationModel, Product,  ProductConnector } from '@spartacus/core';
import { BehaviorSubject, Observable, take } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { SlbOccConnector } from '../connectors/slb-occ.connector';
import { SlbOccEndpoints } from '../config/slb-occ.endpoints';
import { CaseStudies, CaseStudiesSearch, ProductReferenceSearch, ProfileComparisonSearch, SupportedWorkflowsSearch } from '../../utils/interfaces/slb-product-detail';
import { SlbBaseSitesService } from './slb-base-sites.service';
import { INITIAL_CONFIG } from '../../utils/consts';

@Injectable({
  providedIn: 'root',
})
export class SlbProductService {
  private connector = inject(SlbOccConnector);
  private productService = inject(ProductConnector);
  private baseSiteService = inject(SlbBaseSitesService);
  private supportedWorkflows = new BehaviorSubject<SupportedWorkflowsSearch | null>(null);
  private profileComparison = new BehaviorSubject<ProfileComparisonSearch | null>(null);
  private caseStudies = new BehaviorSubject<CaseStudies[]>([]);
  private caseStudiesPagination = new BehaviorSubject<PaginationModel | null>(null);
  private productReferences = new BehaviorSubject<Product[]>([]);
  private productReferencePagination = new BehaviorSubject<PaginationModel | null>(null);

  private currentCaseStudies = this.caseStudies.asObservable();
  private currentCaseStudiesPagination = this.caseStudiesPagination.asObservable();
  private currentProductReference = this.productReferences.asObservable();
  private currentProductReferencePagination = this.productReferencePagination.asObservable();
  private currentSupportedWorkflows = this.supportedWorkflows.asObservable();
  private currentProfileComparison = this.profileComparison.asObservable();

  getProductByCode(productCode: string): Observable<Product | undefined> {
    return this.productService.get(productCode).pipe(
      take(1)
    );
  }

  getProductsByCode(productCode: string): Observable<Product  | undefined> {
    this.getProductCaseStudies(productCode);
    this.getProductReferences(productCode);
    this.getProductSupportedWorkflows(productCode);
    this.getProductProfileComparision(productCode);
    return this.productService.get(productCode).pipe(
      take(1)
    );
  }

  getProductsReference(productCode: string): Observable<ProductReferenceSearch> {
    return this.connector.get(`${SlbOccEndpoints.productReferences}`, { productCode });
  }

  getProductReferences(productCode: string): void {
    this.baseSiteService.getConfigurationByKey(INITIAL_CONFIG.RELATED_EXTENSIONS_MAX_PAGE_SIZE).pipe(
      take(1),
      switchMap((config) =>
        this.connector.get<ProductReferenceSearch>(
          `${SlbOccEndpoints.productReferences}`,
          { productCode },
          {
            fields: 'FULL',
            pageSize: config?.value ?? 50,
            referenceType: 'PLUGIN',
            fromVariants: true,
          },
        ),
      ),
    ).subscribe((productReferencesSearch) => {
      this.productReferences.next(productReferencesSearch.references.map((productReference: { target: any; }) => productReference.target));
      this.productReferencePagination.next(productReferencesSearch.pagination);
    });
  }

  getProductCaseStudies(productCode: string): void {
    this.connector.get<CaseStudiesSearch>(`${SlbOccEndpoints.productCaseStudies}`, { productCode: productCode, currentPage: 0 }).subscribe((caseStudiesSearch) => {
      this.caseStudies.next(caseStudiesSearch.caseStudies);
      this.caseStudiesPagination.next(caseStudiesSearch.pagination);
    });
  }

  getProductSupportedWorkflows(productCode: string): void {
    this.connector.get<SupportedWorkflowsSearch>(`${SlbOccEndpoints.productSupportedWorkflows}`, { productCode: productCode }).subscribe((supportedWorkflows) => {
      this.supportedWorkflows.next(supportedWorkflows);
    });
  }

  getProductProfileComparision(productCode: string): void {
    this.connector.get<ProfileComparisonSearch>(`${SlbOccEndpoints.productProfileComparison}`, { productCode: productCode }).subscribe((profileComparison) => {
      this.profileComparison.next(profileComparison as unknown as ProfileComparisonSearch);
    });
  }

  getReferences(): Observable<Product[]> {
    return this.currentProductReference;
  }

  getSupportedWorkflows(): Observable<any> {
    return this.currentSupportedWorkflows;
  }

  getProfileComparison(): Observable<any> {
    return this.currentProfileComparison;
  }

  getProductReferencesPagination(): Observable<PaginationModel | null> {
    return this.currentProductReferencePagination;
  }

  getMoreProductCaseStudies(productCode: string): void {
    this.connector.get<CaseStudiesSearch>(`${SlbOccEndpoints.productCaseStudies}`, { productCode: productCode, currentPage: (this.caseStudiesPagination.value?.currentPage ?? 0) + 1 }).subscribe((caseStudiesSearch) => {
      this.caseStudies.next([...this.caseStudies.value ,...caseStudiesSearch.caseStudies]);
      this.caseStudiesPagination.next(caseStudiesSearch.pagination);
    });
  }

  getCaseStudies(): Observable<CaseStudies[]> {
    return this.currentCaseStudies;
  }

  getCaseStudiesPagination(): Observable<PaginationModel | null> {
    return this.currentCaseStudiesPagination;
  }

  getAgoraProducts(): Observable<Product[]> {
    return this.connector.get<{ products: Product[] }>(SlbOccEndpoints.agora.products)
      .pipe(
        map(result => result?.products)
      );
  }
}
